import React, { useMemo, useState, useCallback } from 'react';
import {
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TextField,
  Box,
  Typography,
  InputLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FIELD_STYLES } from './fieldStyles';

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  maxSelections,
  placeholder = 'Select items',
  onChange,
  disabled = false,
  sx = {},
  menuProps = {},
  showSelectAll = true,
  searchable = false,
  name = 'records',
  multiple = true,
  label,
  required = false,
  error,
  helperText,
  renderOption,
}) => {
  const [searchText, setSearchText] = useState('');

  const optionMap = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(o.id, o.name));
    return map;
  }, [options]);

  const safeSelected = useMemo(() => {
    return multiple
      ? Array.isArray(selectedValues)
        ? selectedValues
        : []
      : (selectedValues ?? '');
  }, [selectedValues, multiple]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchText) return options;
    const lower = searchText.toLowerCase();
    return options.filter(
      (o) =>
        typeof o?.name === 'string' && o.name.toLowerCase().includes(lower),
    );
  }, [options, searchable, searchText]);

  const isAllSelected = useMemo(
    () =>
      multiple && options.length > 0 && safeSelected.length === options.length,
    [multiple, safeSelected, options],
  );

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value;
      if (multiple) {
        if (value.includes('all')) {
          onChange(
            safeSelected.length === options.length
              ? []
              : options.map((o) => o.id),
          );
        } else {
          onChange(value);
        }
      } else {
        onChange(value);
      }
    },
    [multiple, onChange, options, safeSelected],
  );

  const handleSearchChange = (e) => setSearchText(e.target.value);

  const renderValue = useCallback(
    (selected) => {
      if (multiple) {
        if (!selected?.length) {
          return (
            <Typography sx={{ color: 'text.disabled' }}>
              {placeholder}
            </Typography>
          );
        }
        if (selected.length === options.length && name === 'projects') {
          return 'All projects';
        }
        return (
          <Typography
            noWrap
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {selected
              .map((id) => optionMap.get(id) || '')
              .filter(Boolean)
              .join(', ')}
          </Typography>
        );
      }
      if (!selected) {
        return (
          <Typography sx={{ color: 'text.disabled' }}>{placeholder}</Typography>
        );
      }
      return optionMap.get(selected) || placeholder;
    },
    [multiple, optionMap, options.length, placeholder, name],
  );

  return (
    <Box sx={{ minWidth: 0, gap: 0.25 }}>
      {/* Label */}
      {label && (
        <InputLabel
          required={required}
          sx={{
            position: 'static',
            transform: 'none',
            mb: 0.75,
            '& .MuiFormLabel-asterisk': {
              color: 'error.main',
            },
          }}>
          {label}
        </InputLabel>
      )}

      <FormControl
        sx={{ width: '100%', ...sx }}
        disabled={disabled}
        error={!!error}>
        <Select
          multiple={multiple}
          value={safeSelected}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={renderValue}
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            fontSize: FIELD_STYLES.fontSize,
            minHeight: FIELD_STYLES.minHeight,
            pr: 5,

            '& .MuiSelect-select': {
              padding: `${FIELD_STYLES.paddingY} ${FIELD_STYLES.paddingX} !important`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          MenuProps={{
            PaperProps: { sx: { maxHeight: 250 } },
            autoFocus: false,
            ...menuProps,
          }}
          onClose={() => setSearchText('')}>
          {/* Search */}
          {searchable && (
            <Box
              sx={{
                px: 2,
                py: 1,
                position: 'sticky',
                top: 0,
                bgcolor: 'background.paper',
                zIndex: 1,
              }}>
              <TextField
                placeholder={`Search ${name}...`}
                fullWidth
                value={searchText}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: FIELD_STYLES.minHeight,
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: `${FIELD_STYLES.paddingY} ${FIELD_STYLES.paddingX}`,
                    fontSize: FIELD_STYLES.fontSize,
                  },
                }}
              />
            </Box>
          )}

          {/* Select All */}
          {multiple && showSelectAll && !searchText && options.length > 1 && (
            <MenuItem value="all">
              <Checkbox checked={isAllSelected} />
              <ListItemText primary="Select All" />
            </MenuItem>
          )}

          {/* Options */}
          {filteredOptions.length === 0 ? (
            <MenuItem disabled>
              <ListItemText
                primary={`No ${name} found`}
                sx={{ textAlign: 'center', color: 'text.secondary' }}
              />
            </MenuItem>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = safeSelected.includes(option.id);
              const isDisabled =
                option.disabled ||
                (multiple &&
                  maxSelections !== undefined &&
                  !isSelected &&
                  safeSelected.length >= maxSelections);

              return (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  disabled={isDisabled}>
                  {multiple && (
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      disableRipple
                    />
                  )}
                  <ListItemText
                    primary={
                      renderOption ? (
                        renderOption(option)
                      ) : (
                        <Typography sx={{ textTransform: 'none' }}>
                          {option.name}
                        </Typography>
                      )
                    }
                  />
                </MenuItem>
              );
            })
          )}
        </Select>

        {(error || helperText) && (
          <FormHelperText sx={{ m: 0 }}>{error || helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default React.memo(MultiSelectDropdown);
